import Cookies from 'js-cookie';
import { defineStore } from 'pinia';
import { MessagePlugin } from 'tdesign-vue-next';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import * as api from '@/api/auth';
import { usePermissionStore } from '@/store';
import type { UserInfo } from '@/types/common/ui';
import * as auth from '@/utils/auth';

/**
 * 用户信息
 */
export const useUserStore = defineStore(
  'user',
  () => {
    const permissionStore = usePermissionStore();
    const router = useRouter();
    /**
     * 默认令牌
     */
    const token = ref<string>();

    /**
     * 用户信息
     */
    const userInfo = ref<UserInfo | null>(null);

    /**
     * 用户权限
     */
    const roles = computed(() => userInfo?.value?.roles ?? []);

    /**
     * 设置用户令牌
     *
     * @param t 要设置的令牌
     */
    function setToken(t: string) {
      auth.setToken(t);
    }

    /**
     * 设置用户信息
     *
     * @param info 要设置的用户信息
     */
    function setUserInfo(info: UserInfo | null) {
      userInfo.value = info;
    }

    /**
     * 退出登录
     */
    async function logout() {
      permissionStore.resetPermission();
      return api.logout().then(() => {
        setUserInfo(null);
        auth.removeToken();
      });
    }

    async function getVersion() {
      const res = await fetch(`/version.json?_t=${Date.now()}`, { cache: 'no-store' });
      const data = await res.json();
      return data.version;
    }

    async function setVersion(version: string) {
      localStorage.setItem('app_version', version);
    }

    async function checkAppVersion() {
      try {
        const remoteVersion = await getVersion();
        const localVersion = localStorage.getItem('app_version');

        if (!localVersion) {
          setVersion(remoteVersion);
        }
        if (remoteVersion !== localVersion) {
          if (remoteVersion !== localVersion) {
            // 清除 localStorage
            localStorage.clear();
            // 清除 sessionStorage
            sessionStorage.clear();
            // 清除所有 cookie
            Object.keys(Cookies.get()).forEach((cookieName) => {
              Cookies.remove(cookieName);
            });
            MessagePlugin.error('检测到版本更新，请重新登录');
            logout();
            router.push('/login');
          }
        }
      } catch (e) {
        console.error('版本检测失败', e);
      }
    }

    return {
      token,
      userInfo,
      roles,
      setToken,
      setUserInfo,
      logout,
      checkAppVersion,
      getVersion,
      setVersion,
    };
  },
  {
    persist: {
      key: 'user',
    },
  },
);
